import React from "react";
import { rowStatus } from "../../../features/queue/queueData";
import { formatDueDate, isDueDateLate } from "../../../utils/dueDate";
import { fmtSeconds, intervalToSeconds } from "../../../utils/multiOpFlow";

// One queue row as a phone card (layout only). The words and chips are the desktop list's.

const STATUS_CLASS = {
  "In Progress": "jq-status-progress",
  Paused: "jq-status-paused",
  Maintenance: "jq-status-maint",
  Completed: "jq-status-done",
  Shipped: "jq-status-done",
  Cancelled: "jq-status-cancelled",
};

export function StatusPill({ status }) {
  return <span className={`jq-status ${STATUS_CLASS[status] || "jq-status-pending"}`}>{status || "Pending"}</span>;
}

/** The row's remake target (a replacement job): {qty, job_num, station} or null. */
function remakeTarget(row) {
  try {
    return row.remake_target_json ? JSON.parse(row.remake_target_json) : null;
  } catch (e) {
    return null;
  }
}

/** Chips every queue card and task sheet shows: next up, op, set, holds, blocks. */
export function TaskChips({ row, nextUp = false }) {
  const rmk = remakeTarget(row);
  const hold = row.remake_hold && row.remake_hold.level !== "info" ? row.remake_hold : null;
  const chips = [];
  if (nextUp) chips.push(<span key="next" className="jq-tag jq-tag-next">Next up</span>);
  if (row.op_count) {
    chips.push(
      <span key="op" className="jq-tag">
        {rowStatus(row) === "Completed" ? `All ${row.op_count} ops done` : `Op ${row.current_op || 1}/${row.op_count}`}
      </span>
    );
  }
  if (row.sub_process) chips.push(<span key="sub" className="jq-tag">{row.sub_process}</span>);
  if (row.set_identifier) chips.push(<span key="set" className="jq-tag">SET {row.set_identifier}</span>);
  if (hold) {
    chips.push(
      <span key="hold" className={`jq-tag ${hold.level === "block" ? "jq-tag-block" : "jq-tag-warn"}`} title={hold.message}>
        {hold.level === "block" ? "⛔ WAITING ON REPLACEMENTS" : "⏳ REPLACEMENTS ON THE WAY"}
      </span>
    );
  }
  if (rmk) {
    const at = rmk.station === "Welding" || rmk.station === "Fabrication" ? "Welding" : rmk.station || "Join";
    chips.push(
      <span key="rmk" className="jq-tag jq-tag-remake" title={`Replacement parts: deliver ${rmk.qty} pc(s) to Job #${rmk.job_num} at ${at}. Do NOT pack.`}>
        🔁 REMAKE → #{rmk.job_num} {at}
      </span>
    );
  }
  if (row.blocked) chips.push(<span key="blocked" className="jq-tag jq-tag-block">Blocked</span>);
  if (row.contains_obsolete_parts) chips.push(<span key="obs" className="jq-tag jq-tag-block">Obsolete part/rev</span>);
  if ((row.pre_shipped_qty || 0) > 0) chips.push(<span key="pre" className="jq-tag">Pre-shipped {row.pre_shipped_qty}</span>);
  if (row.priority_level === 1) chips.push(<span key="p1" className="jq-tag jq-tag-block">P1</span>);
  if (row.priority_level === 0) chips.push(<span key="maint" className="jq-tag">Maintenance</span>);
  return chips.length ? <div className="jq-qcard-chips">{chips}</div> : null;
}

export default function QueueTaskCard({ row, nextUp = false }) {
  const due = row.task_due_date ?? row.due_date;
  const qty = row.remaining ?? row.quantity;
  const est = intervalToSeconds(row.estimated_time);
  return (
    <>
      <div className="jq-qcard-top">
        <span className="jq-qcard-job">Job #{row.master_job_num}</span>
        <span className="jq-muted">Task {row.task_id}</span>
        <StatusPill status={rowStatus(row)} />
      </div>
      <div className="jq-qcard-title">{row.product_description || row.job_description || row.maintenance_title}</div>
      <div className="jq-qcard-meta">
        {row.customer_name && <span>{row.customer_name}</span>}
        {due && <span className={isDueDateLate(due) ? "text-danger fw-semibold" : undefined}>Due {formatDueDate(due)}</span>}
        {qty != null && <span>Qty {qty}</span>}
        {est > 0 && <span>Est. {fmtSeconds(est)}</span>}
      </div>
      <TaskChips row={row} nextUp={nextUp} />
    </>
  );
}

/** Time left on the estimate, ticking while the task runs (the old phone queue's countdown). */
export function TimeLeft({ row, status, estimated = row.estimated_time }) {
  const [now, setNow] = React.useState(() => Date.now());
  const running = status === "In Progress";
  React.useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [running]);
  const est = intervalToSeconds(estimated);
  let used = Number(row.elapsed_time_seconds) || 0;
  if (running && row.last_start_time) used += Math.max(0, (now - new Date(row.last_start_time).getTime()) / 1000);
  const left = est - used;
  const abs = Math.abs(left);
  const text = `${left < 0 ? "-" : ""}${Math.floor(abs / 3600)}h ${Math.floor((abs % 3600) / 60)}m ${Math.floor(abs % 60)}s`;
  return <span className={left < 0 ? "text-danger" : undefined}>{text}</span>;
}
